import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Progress } from './ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { useToast } from '../hooks/use-toast';
import PageHeader from './ui/PageHeader';
import { 
  Upload, Camera, AlertTriangle, CheckCircle, Shield, FileText, Zap, 
  Cpu, Eye, Sparkles, Video, Files, FileSearch, History, 
  Lightbulb, Users, Wrench, Bell, Download, Link2, TrendingUp,
  Play, Loader2, X, ChevronRight, BarChart3, PieChart as PieChartIcon,
  Clock, Target, AlertCircle, Check
} from 'lucide-react';
import { 
  PieChart as RechartsPie, Pie, Cell, BarChart, Bar, XAxis, YAxis, 
  CartesianGrid, Tooltip, Legend, ResponsiveContainer, RadarChart, 
  PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar, LineChart, Line, Area, AreaChart
} from 'recharts';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const COLORS = {
  critical_risk: '#DC2626',
  imminent_danger: '#991B1B',
  high_risk: '#EA580C',
  moderate_risk: '#F59E0B',
  low_risk: '#10B981',
  informational: '#3B82F6'
};

const ANALYSIS_TYPES = [
  { id: 'image', name: 'Single Image', icon: Camera, description: 'Analyze one workplace photo', color: 'blue' },
  { id: 'batch', name: 'Batch Images', icon: Files, description: 'Analyze multiple images at once', color: 'purple' },
  { id: 'video', name: 'Video Analysis', icon: Video, description: 'Extract and analyze video frames', color: 'green' },
  { id: 'document', name: 'Document/PDF', icon: FileSearch, description: 'Analyze safety documents', color: 'orange' },
  { id: 'incident', name: 'Incident Recreation', icon: Target, description: 'Reconstruct incidents from photos', color: 'red' },
];

const AdvancedSafetyAI = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  
  // Analysis state
  const [activeTab, setActiveTab] = useState('analyze');
  const [analysisType, setAnalysisType] = useState('image');
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [previewUrls, setPreviewUrls] = useState([]);
  const [loading, setLoading] = useState(false);
  const [analysisResults, setAnalysisResults] = useState(null);
  
  // Incident recreation
  const [incidentDescription, setIncidentDescription] = useState('');
  const [incidentDate, setIncidentDate] = useState('');
  
  // Document analysis
  const [documentType, setDocumentType] = useState('safety document');
  
  // Historical data
  const [analyses, setAnalyses] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [patterns, setPatterns] = useState(null);
  
  // Video settings
  const [maxFrames, setMaxFrames] = useState(8);
  
  // Audit linking
  const [availableAudits, setAvailableAudits] = useState([]);
  const [selectedAuditId, setSelectedAuditId] = useState('');
  
  // User info (would come from auth context in real app)
  const [userId] = useState('current-user-id');
  const [companyId] = useState('current-company-id');
  
  const [isDragging, setIsDragging] = useState(false);

  const fetchAnalyses = async () => {
    try {
      const response = await axios.get(`${API}/safety-ai/analyses?limit=20`);
      setAnalyses(response.data.analyses || []);
    } catch (error) {
      console.error('Error fetching analyses:', error);
    }
  };

  const fetchAlerts = async () => {
    try {
      const response = await axios.get(`${API}/safety-ai/alerts?unacknowledged_only=true`);
      setAlerts(response.data.alerts || []);
    } catch (error) {
      console.error('Error fetching alerts:', error);
    }
  };

  const fetchAudits = async () => {
    try {
      const response = await axios.get(`${API}/audits`);
      setAvailableAudits(response.data || []);
    } catch (error) {
      console.error('Error fetching audits:', error);
    }
  };

  const loadData = useCallback(async () => {
    setLoading(true);
    await Promise.all([
      fetchAnalyses(),
      fetchAlerts(),
      fetchAudits()
    ]);
    setLoading(false);
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const fetchPatterns = async () => {
    try {
      const response = await axios.get(`${API}/safety-ai/historical-patterns/${companyId}`);
      setPatterns(response.data);
    } catch (error) {
      console.error('Error fetching patterns:', error);
    }
  };

  const handleFileSelect = (event) => {
    const files = Array.from(event.target.files);
    processFiles(files);
  };

  const processFiles = (files) => {
    const validFiles = files.filter(file => {
      if (analysisType === 'document') {
        return file.type === 'application/pdf' || file.type.startsWith('image/');
      }
      if (analysisType === 'video') {
        return file.type.startsWith('video/');
      }
      return file.type.startsWith('image/');
    });

    if (validFiles.length === 0) {
      toast({
        title: "Invalid Files",
        description: "Please select valid files for the selected analysis type",
        variant: "destructive"
      });
      return;
    }

    // Limit files for batch
    const maxFiles = analysisType === 'batch' ? 10 : (analysisType === 'incident' ? 5 : 1);
    const filesToUse = validFiles.slice(0, maxFiles);

    setSelectedFiles(filesToUse);
    
    // Create preview URLs
    const urls = filesToUse.map(file => URL.createObjectURL(file));
    setPreviewUrls(urls);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const files = Array.from(e.dataTransfer.files);
    processFiles(files);
  };

  const clearFiles = () => {
    setSelectedFiles([]);
    previewUrls.forEach(url => URL.revokeObjectURL(url));
    setPreviewUrls([]);
    setAnalysisResults(null);
  };

  const runAnalysis = async () => {
    if (selectedFiles.length === 0) {
      toast({
        title: "No Files Selected",
        description: "Please select files to analyze",
        variant: "destructive"
      });
      return;
    }

    setLoading(true);
    setAnalysisResults(null);

    try {
      const formData = new FormData();
      formData.append('user_id', userId);
      formData.append('company_id', companyId);
      
      if (selectedAuditId) {
        formData.append('audit_id', selectedAuditId);
      }

      let endpoint = '';

      switch (analysisType) {
        case 'image':
          endpoint = '/safety-ai/analyze/image';
          formData.append('file', selectedFiles[0]);
          formData.append('include_equipment', 'true');
          formData.append('include_workers', 'true');
          break;

        case 'batch':
          endpoint = '/safety-ai/analyze/batch';
          selectedFiles.forEach(file => formData.append('files', file));
          break;

        case 'video':
          endpoint = '/safety-ai/analyze/video';
          formData.append('file', selectedFiles[0]);
          formData.append('max_frames', maxFrames.toString());
          break;

        case 'document':
          endpoint = '/safety-ai/analyze/document';
          formData.append('file', selectedFiles[0]);
          formData.append('document_type', documentType);
          break;

        case 'incident':
          endpoint = '/safety-ai/analyze/incident-recreation';
          selectedFiles.forEach(file => formData.append('files', file));
          formData.append('incident_description', incidentDescription);
          if (incidentDate) formData.append('incident_date', incidentDate);
          break;

        default:
          throw new Error('Unknown analysis type');
      }

      const response = await axios.post(`${API}${endpoint}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 120000 // 2 minute timeout for video analysis
      });

      setAnalysisResults(response.data);
      
      toast({
        title: "Analysis Complete! ✅",
        description: `Found ${response.data.violations_count || response.data.total_violations || 0} violations`
      });

      // Refresh analyses list
      fetchAnalyses();
      fetchAlerts();

    } catch (error) {
      console.error('Analysis error:', error);
      toast({
        title: "Analysis Failed",
        description: error.response?.data?.detail || error.message || "Please try again",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const acknowledgeAlert = async (alertId) => {
    try {
      await axios.put(`${API}/safety-ai/alerts/${alertId}/acknowledge?user_id=${userId}`);
      toast({ title: "Alert Acknowledged" });
      fetchAlerts();
    } catch (error) {
      toast({ title: "Error", description: "Failed to acknowledge alert", variant: "destructive" });
    }
  };

  const generateReport = async (analysisId) => {
    try {
      const response = await axios.post(`${API}/safety-ai/generate-report/${analysisId}`);
      
      // Create download link
      const linkSource = `data:application/pdf;base64,${response.data.pdf_base64}`;
      const downloadLink = document.createElement('a');
      downloadLink.href = linkSource;
      downloadLink.download = `safety-report-${analysisId}.pdf`;
      downloadLink.click();
      
      toast({ title: "Report Downloaded!" });
    } catch (error) {
      toast({ title: "Error", description: "Failed to generate report", variant: "destructive" });
    }
  };

  const linkToAudit = async (analysisId, auditId) => {
    try {
      await axios.post(`${API}/safety-ai/link-to-audit?analysis_id=${analysisId}&audit_id=${auditId}`);
      toast({ title: "Success", description: "Analysis linked to audit" });
    } catch (error) {
      toast({ title: "Error", description: "Failed to link to audit", variant: "destructive" });
    }
  };

  const getSeverityColor = (severity) => {
    const colors = {
      'critical_risk': 'bg-red-100 text-red-800 border-red-300',
      'imminent_danger': 'bg-red-200 text-red-900 border-red-400',
      'high_risk': 'bg-orange-100 text-orange-800 border-orange-300',
      'moderate_risk': 'bg-yellow-100 text-yellow-800 border-yellow-300',
      'low_risk': 'bg-green-100 text-green-800 border-green-300',
      'informational': 'bg-blue-100 text-blue-800 border-blue-300'
    };
    return colors[severity] || 'bg-gray-100 text-gray-800';
  };

  const getPriorityColor = (priority) => {
    const colors = {
      'critical': 'bg-red-500 text-white',
      'high': 'bg-orange-500 text-white',
      'medium': 'bg-yellow-500 text-black',
      'low': 'bg-green-500 text-white'
    };
    return colors[priority] || 'bg-gray-500 text-white';
  };

  // Render analysis type selector
  const renderAnalysisTypeSelector = () => (
    <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
      {ANALYSIS_TYPES.map((type) => (
        <button
          key={type.id}
          onClick={() => {
            setAnalysisType(type.id);
            clearFiles();
          }}
          className={`p-4 rounded-xl border-2 transition-all ${
            analysisType === type.id 
              ? `border-${type.color}-500 bg-${type.color}-50 shadow-md` 
              : 'border-gray-200 hover:border-gray-300 bg-white'
          }`}
        >
          <type.icon className={`w-6 h-6 mx-auto mb-2 ${
            analysisType === type.id ? `text-${type.color}-600` : 'text-gray-400'
          }`} />
          <p className={`text-sm font-medium ${
            analysisType === type.id ? `text-${type.color}-700` : 'text-gray-700'
          }`}>{type.name}</p>
          <p className="text-xs text-gray-500 mt-1 hidden md:block">{type.description}</p>
        </button>
      ))}
    </div>
  );

  // Render file upload area
  const renderUploadArea = () => (
    <Card className="mb-6">
      <CardContent className="p-6">
        <div
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          className={`border-2 border-dashed rounded-xl p-8 text-center transition-all ${
            isDragging 
              ? 'border-blue-500 bg-blue-50' 
              : 'border-gray-300 hover:border-gray-400 bg-gray-50'
          }`}
        >
          {selectedFiles.length === 0 ? (
            <>
              <Upload className="w-12 h-12 mx-auto text-gray-400 mb-4" />
              <p className="text-lg font-medium text-gray-700 mb-2">
                {analysisType === 'video' ? 'Drop video file here' :
                 analysisType === 'document' ? 'Drop PDF or document image here' :
                 analysisType === 'batch' ? 'Drop multiple images here (max 10)' :
                 analysisType === 'incident' ? 'Drop incident scene photos here (max 5)' :
                 'Drop image file here'}
              </p>
              <p className="text-sm text-gray-500 mb-4">or click to browse</p>
              <input
                type="file"
                onChange={handleFileSelect}
                accept={
                  analysisType === 'video' ? 'video/*' :
                  analysisType === 'document' ? '.pdf,image/*' :
                  'image/*'
                }
                multiple={analysisType === 'batch' || analysisType === 'incident'}
                className="hidden"
                id="file-upload"
              />
              <label htmlFor="file-upload">
                <Button variant="outline" className="cursor-pointer" asChild>
                  <span>Select Files</span>
                </Button>
              </label>
            </>
          ) : (
            <div>
              <div className="flex flex-wrap gap-4 justify-center mb-4">
                {previewUrls.map((url, idx) => (
                  <div key={idx} className="relative">
                    {analysisType === 'video' ? (
                      <div className="w-32 h-24 bg-gray-200 rounded-lg flex items-center justify-center">
                        <Video className="w-8 h-8 text-gray-500" />
                      </div>
                    ) : analysisType === 'document' && selectedFiles[idx]?.type === 'application/pdf' ? (
                      <div className="w-32 h-24 bg-red-100 rounded-lg flex items-center justify-center">
                        <FileText className="w-8 h-8 text-red-500" />
                      </div>
                    ) : (
                      <img 
                        src={url} 
                        alt={`Preview ${idx + 1}`} 
                        className="w-32 h-24 object-cover rounded-lg"
                      />
                    )}
                    <span className="absolute -top-2 -right-2 bg-blue-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                      {idx + 1}
                    </span>
                  </div>
                ))}
              </div>
              <p className="text-sm text-gray-600 mb-3">
                {selectedFiles.length} file(s) selected
              </p>
              <Button variant="outline" size="sm" onClick={clearFiles}>
                <X className="w-4 h-4 mr-1" /> Clear
              </Button>
            </div>
          )}
        </div>

        {/* Additional options based on analysis type */}
        {analysisType === 'video' && selectedFiles.length > 0 && (
          <div className="mt-4 p-4 bg-gray-50 rounded-lg">
            <Label>Frames to Extract: {maxFrames}</Label>
            <input
              type="range"
              min="4"
              max="16"
              value={maxFrames}
              onChange={(e) => setMaxFrames(parseInt(e.target.value))}
              className="w-full mt-2"
            />
            <p className="text-xs text-gray-500 mt-1">More frames = more thorough but slower analysis</p>
          </div>
        )}

        {analysisType === 'document' && selectedFiles.length > 0 && (
          <div className="mt-4 p-4 bg-gray-50 rounded-lg">
            <Label>Document Type</Label>
            <select
              value={documentType}
              onChange={(e) => setDocumentType(e.target.value)}
              className="w-full mt-2 p-2 border rounded-md"
            >
              <option value="safety document">General Safety Document</option>
              <option value="SDS sheet">Safety Data Sheet (SDS)</option>
              <option value="JSA">Job Safety Analysis (JSA)</option>
              <option value="permit">Work Permit</option>
              <option value="inspection report">Inspection Report</option>
              <option value="training record">Training Record</option>
            </select>
          </div>
        )}

        {analysisType === 'incident' && selectedFiles.length > 0 && (
          <div className="mt-4 p-4 bg-red-50 rounded-lg space-y-4">
            <div>
              <Label className="text-red-800">Incident Description *</Label>
              <Textarea
                value={incidentDescription}
                onChange={(e) => setIncidentDescription(e.target.value)}
                placeholder="Describe what happened, injuries sustained, equipment involved..."
                className="mt-2"
                rows={3}
              />
            </div>
            <div>
              <Label className="text-red-800">Incident Date (optional)</Label>
              <Input
                type="date"
                value={incidentDate}
                onChange={(e) => setIncidentDate(e.target.value)}
                className="mt-2"
              />
            </div>
          </div>
        )}

        {/* Audit linking option */}
        {selectedFiles.length > 0 && (
          <div className="mt-4 p-4 bg-blue-50 rounded-lg">
            <Label className="flex items-center gap-2">
              <Link2 className="w-4 h-4" />
              Link to Audit (optional)
            </Label>
            <select
              value={selectedAuditId}
              onChange={(e) => setSelectedAuditId(e.target.value)}
              className="w-full mt-2 p-2 border rounded-md"
            >
              <option value="">No audit link</option>
              {availableAudits.map(audit => (
                <option key={audit.id} value={audit.id}>
                  {audit.name} - {audit.site_location || 'No location'}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Analyze button */}
        {selectedFiles.length > 0 && (
          <Button
            onClick={runAnalysis}
            disabled={loading || (analysisType === 'incident' && !incidentDescription)}
            className="w-full mt-4 h-12 text-lg"
          >
            {loading ? (
              <>
                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                Analyzing with GPT-4o Vision...
              </>
            ) : (
              <>
                <Sparkles className="w-5 h-5 mr-2" />
                Run AI Analysis
              </>
            )}
          </Button>
        )}
      </CardContent>
    </Card>
  );

  // Render analysis results
  const renderResults = () => {
    if (!analysisResults) return null;

    return (
      <div className="space-y-6">
        {/* Summary Card */}
        <Card className="border-2 border-blue-200 bg-gradient-to-r from-blue-50 to-indigo-50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Cpu className="w-6 h-6 text-blue-600" />
              AI Analysis Results
            </CardTitle>
            <CardDescription>
              Processed in {analysisResults.processing_time_seconds?.toFixed(1) || '?'}s
              {analysisResults.frames_analyzed && ` • ${analysisResults.frames_analyzed} frames analyzed`}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <div className="text-center p-4 bg-white rounded-xl shadow-sm">
                <p className="text-3xl font-bold text-blue-600">
                  {analysisResults.compliance_percentage?.toFixed(0) || 0}%
                </p>
                <p className="text-sm text-gray-600">Compliance Rate</p>
              </div>
              <div className="text-center p-4 bg-white rounded-xl shadow-sm">
                <p className="text-3xl font-bold text-red-600">
                  {analysisResults.violations_count || analysisResults.total_violations || 0}
                </p>
                <p className="text-sm text-gray-600">Violations Found</p>
              </div>
              <div className="text-center p-4 bg-white rounded-xl shadow-sm">
                <p className="text-3xl font-bold text-purple-600">
                  {analysisResults.workers_detected || analysisResults.total_workers_detected || 0}
                </p>
                <p className="text-sm text-gray-600">Workers Detected</p>
              </div>
              <div className="text-center p-4 bg-white rounded-xl shadow-sm">
                <p className="text-3xl font-bold text-green-600">
                  {analysisResults.ppe_compliance_rate?.toFixed(0) || 100}%
                </p>
                <p className="text-sm text-gray-600">PPE Compliance</p>
              </div>
            </div>

            {analysisResults.ai_summary && (
              <div className="p-4 bg-white rounded-xl border mb-4">
                <h4 className="font-semibold text-gray-800 mb-2 flex items-center gap-2">
                  <Eye className="w-4 h-4" /> AI Assessment
                </h4>
                <p className="text-gray-700">{analysisResults.ai_summary}</p>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex gap-2 flex-wrap">
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => generateReport(analysisResults.analysis_id)}
              >
                <Download className="w-4 h-4 mr-1" /> Download PDF Report
              </Button>
              {availableAudits.length > 0 && !selectedAuditId && (
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => {
                    const auditId = availableAudits[0]?.id;
                    if (auditId) linkToAudit(analysisResults.analysis_id, auditId);
                  }}
                >
                  <Link2 className="w-4 h-4 mr-1" /> Link to Audit
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Violations */}
        {analysisResults.violations && analysisResults.violations.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-red-700">
                <AlertTriangle className="w-5 h-5" />
                Violations Detected ({analysisResults.violations.length})
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {analysisResults.violations.map((violation, idx) => (
                <div 
                  key={idx} 
                  className={`p-4 rounded-lg border-l-4 ${getSeverityColor(violation.severity)}`}
                >
                  <div className="flex items-start justify-between">
                    <div>
                      <h4 className="font-semibold">
                        {violation.type || violation.violation_type}
                      </h4>
                      <p className="text-sm mt-1">{violation.description}</p>
                      <div className="flex gap-2 mt-2 flex-wrap">
                        <Badge variant="outline">{violation.osha_reference}</Badge>
                        <Badge className={getSeverityColor(violation.severity)}>
                          {violation.severity?.replace(/_/g, ' ')}
                        </Badge>
                        {violation.confidence && (
                          <Badge variant="secondary">
                            {(violation.confidence * 100).toFixed(0)}% confidence
                          </Badge>
                        )}
                        {violation.immediate_action && (
                          <Badge className="bg-red-600 text-white">Immediate Action Required</Badge>
                        )}
                      </div>
                    </div>
                  </div>
                  {violation.corrective_actions && violation.corrective_actions.length > 0 && (
                    <div className="mt-3 pt-3 border-t">
                      <p className="text-sm font-medium text-gray-700">Corrective Actions:</p>
                      <ul className="text-sm text-gray-600 mt-1 list-disc list-inside">
                        {violation.corrective_actions.map((action, i) => (
                          <li key={i}>{action}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              ))}
            </CardContent>
          </Card>
        )}

        {/* Workers */}
        {analysisResults.workers && analysisResults.workers.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-purple-700">
                <Users className="w-5 h-5" />
                Workers Detected ({analysisResults.workers.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid gap-4 md:grid-cols-2">
                {analysisResults.workers.map((worker, idx) => (
                  <div key={idx} className="p-4 bg-gray-50 rounded-lg">
                    <div className="flex items-center justify-between mb-3">
                      <span className="font-medium">Worker {idx + 1}</span>
                      <Badge className={worker.compliance_score >= 0.8 ? 'bg-green-500' : worker.compliance_score >= 0.5 ? 'bg-yellow-500' : 'bg-red-500'}>
                        {(worker.compliance_score * 100).toFixed(0)}% PPE Compliant
                      </Badge>
                    </div>
                    <p className="text-sm text-gray-600 mb-2">Location: {worker.location_in_image}</p>
                    {worker.ppe_status && (
                      <div className="flex flex-wrap gap-1">
                        {Object.entries(worker.ppe_status).map(([item, status]) => (
                          <Badge 
                            key={item} 
                            variant="outline"
                            className={status ? 'border-green-500 text-green-700' : 'border-red-500 text-red-700'}
                          >
                            {status ? <Check className="w-3 h-3 mr-1" /> : <X className="w-3 h-3 mr-1" />}
                            {item.replace(/_/g, ' ')}
                          </Badge>
                        ))}
                      </div>
                    )}
                    {worker.unsafe_behavior && (
                      <p className="text-sm text-red-600 mt-2">
                        ⚠️ {worker.unsafe_behavior}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Equipment */}
        {analysisResults.equipment && analysisResults.equipment.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-orange-700">
                <Wrench className="w-5 h-5" />
                Equipment Detected ({analysisResults.equipment.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid gap-4 md:grid-cols-2">
                {analysisResults.equipment.map((equip, idx) => (
                  <div key={idx} className="p-4 bg-gray-50 rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-medium">{equip.equipment_type}</span>
                      <Badge className={
                        equip.compliance_status === 'compliant' ? 'bg-green-500' : 
                        equip.compliance_status === 'needs_inspection' ? 'bg-yellow-500' : 'bg-red-500'
                      }>
                        {equip.compliance_status?.replace(/_/g, ' ')}
                      </Badge>
                    </div>
                    {equip.brand_model && (
                      <p className="text-sm text-gray-600">Model: {equip.brand_model}</p>
                    )}
                    <p className="text-sm text-gray-600">Condition: {equip.condition}</p>
                    {equip.issues_found && equip.issues_found.length > 0 && (
                      <div className="mt-2">
                        <p className="text-sm font-medium text-red-700">Issues:</p>
                        <ul className="text-sm text-red-600 list-disc list-inside">
                          {equip.issues_found.map((issue, i) => (
                            <li key={i}>{issue}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Recommendations */}
        {analysisResults.recommendations && analysisResults.recommendations.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-blue-700">
                <Lightbulb className="w-5 h-5" />
                Smart Recommendations
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {analysisResults.recommendations.map((rec, idx) => (
                <div key={idx} className="p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg">
                  <div className="flex items-start gap-3">
                    <Badge className={getPriorityColor(rec.priority)}>
                      {rec.priority}
                    </Badge>
                    <div className="flex-1">
                      <h4 className="font-semibold text-gray-800">{rec.title}</h4>
                      <p className="text-sm text-gray-600 mt-1">{rec.description}</p>
                      {rec.action_items && rec.action_items.length > 0 && (
                        <div className="mt-2">
                          <p className="text-xs font-medium text-gray-500">Action Items:</p>
                          <ul className="text-sm text-gray-700 mt-1">
                            {rec.action_items.map((item, i) => (
                              <li key={i} className="flex items-center gap-2">
                                <ChevronRight className="w-3 h-3 text-blue-500" />
                                {item}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        )}

        {/* Incident Recreation Results */}
        {analysisResults.analysis && analysisType === 'incident' && (
          <Card className="border-red-200">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-red-700">
                <Target className="w-5 h-5" />
                Incident Reconstruction
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {analysisResults.analysis.incident_summary && (
                <div className="p-4 bg-red-50 rounded-lg">
                  <h4 className="font-semibold text-red-800 mb-2">Summary</h4>
                  <p className="text-gray-700">{analysisResults.analysis.incident_summary}</p>
                </div>
              )}
              
              {analysisResults.analysis.sequence_of_events && (
                <div>
                  <h4 className="font-semibold text-gray-800 mb-3">Sequence of Events</h4>
                  <div className="space-y-3">
                    {analysisResults.analysis.sequence_of_events.map((event, idx) => (
                      <div key={idx} className="flex gap-3">
                        <div className="w-8 h-8 rounded-full bg-red-100 text-red-700 flex items-center justify-center font-bold text-sm">
                          {event.step}
                        </div>
                        <div className="flex-1 p-3 bg-gray-50 rounded-lg">
                          <p className="font-medium">{event.description}</p>
                          <p className="text-sm text-gray-500 mt-1">Evidence: {event.evidence}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {analysisResults.analysis.root_cause_analysis && (
                <div className="p-4 bg-orange-50 rounded-lg">
                  <h4 className="font-semibold text-orange-800 mb-2">Root Cause Analysis</h4>
                  <p className="text-gray-700 mb-2">
                    <strong>Primary Cause:</strong> {analysisResults.analysis.root_cause_analysis.primary_cause}
                  </p>
                  {analysisResults.analysis.root_cause_analysis.contributing_factors && (
                    <div>
                      <p className="text-sm font-medium text-gray-600">Contributing Factors:</p>
                      <ul className="list-disc list-inside text-sm text-gray-600">
                        {analysisResults.analysis.root_cause_analysis.contributing_factors.map((f, i) => (
                          <li key={i}>{f}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    );
  };

  // Render alerts panel
  const renderAlerts = () => (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Bell className="w-5 h-5 text-red-600" />
          Safety Alerts
          {alerts.length > 0 && (
            <Badge className="bg-red-500 text-white">{alerts.length}</Badge>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {alerts.length === 0 ? (
          <p className="text-gray-500 text-center py-8">No unacknowledged alerts</p>
        ) : (
          <div className="space-y-3">
            {alerts.map((alert) => (
              <div key={alert.id} className="p-4 bg-red-50 border border-red-200 rounded-lg">
                <div className="flex items-start justify-between">
                  <div>
                    <Badge className={getPriorityColor(alert.priority)}>{alert.priority}</Badge>
                    <h4 className="font-semibold text-red-800 mt-2">{alert.title}</h4>
                    <p className="text-sm text-gray-600 mt-1">{alert.description}</p>
                    <p className="text-xs text-gray-400 mt-2">
                      {new Date(alert.created_at).toLocaleString()}
                    </p>
                  </div>
                  <Button 
                    size="sm" 
                    variant="outline"
                    onClick={() => acknowledgeAlert(alert.id)}
                  >
                    <Check className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );

  // Render history
  const renderHistory = () => (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold">Analysis History</h3>
        <Button variant="outline" size="sm" onClick={fetchPatterns}>
          <TrendingUp className="w-4 h-4 mr-1" /> View Patterns
        </Button>
      </div>
      
      {patterns && (
        <Card className="bg-gradient-to-r from-purple-50 to-indigo-50 mb-4">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-purple-700">
              <History className="w-5 h-5" />
              Historical Patterns
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-gray-600 mb-4">
              Based on {patterns.analyses_reviewed} past analyses
            </p>
            {patterns.patterns && patterns.patterns.length > 0 && (
              <div className="space-y-3">
                {patterns.patterns.slice(0, 3).map((pattern, idx) => (
                  <div key={idx} className="p-3 bg-white rounded-lg">
                    <div className="flex items-center justify-between">
                      <span className="font-medium">{pattern.pattern_type.replace(/_/g, ' ')}</span>
                      <Badge variant="outline">
                        {pattern.trend} trend
                      </Badge>
                    </div>
                    <p className="text-sm text-gray-500 mt-1">
                      Frequency: {pattern.frequency} | Risk: {(pattern.risk_prediction * 100).toFixed(0)}%
                    </p>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      <div className="grid gap-4">
        {analyses.map((analysis) => (
          <Card key={analysis.id} className="hover:shadow-md transition-shadow cursor-pointer">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <div className="flex items-center gap-2">
                    <Badge variant="outline">{analysis.analysis_type}</Badge>
                    <span className="text-sm text-gray-500">
                      {analysis.file_names?.[0] || analysis.file_name || 'Analysis'}
                    </span>
                  </div>
                  <div className="flex items-center gap-4 mt-2 text-sm">
                    <span className="text-blue-600 font-medium">
                      {analysis.compliance_percentage?.toFixed(0) || 0}% compliance
                    </span>
                    <span className="text-red-600">
                      {analysis.total_violations || analysis.violations?.length || 0} violations
                    </span>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Button 
                    size="sm" 
                    variant="ghost"
                    onClick={() => generateReport(analysis.id)}
                  >
                    <Download className="w-4 h-4" />
                  </Button>
                  <span className="text-xs text-gray-400">
                    {new Date(analysis.created_at).toLocaleDateString()}
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
        {analyses.length === 0 && (
          <p className="text-gray-500 text-center py-8">No analyses yet. Run your first analysis above!</p>
        )}
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Universal Page Header */}
      <PageHeader
        title="Advanced Safety AI"
        subtitle="AI-powered workplace safety analysis with GPT-4o Vision, video analysis & predictive insights"
        icon={Cpu}
        variant="blue"
        onRefresh={loadData}
        loading={loading}
      />

      {/* Main Content Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid grid-cols-3 w-full max-w-md">
          <TabsTrigger value="analyze" className="flex items-center gap-2">
            <Cpu className="w-4 h-4" />
            <span className="hidden sm:inline">Analyze</span>
          </TabsTrigger>
          <TabsTrigger value="alerts" className="flex items-center gap-2">
            <Bell className="w-4 h-4" />
            <span className="hidden sm:inline">Alerts</span>
            {alerts.length > 0 && (
              <Badge className="bg-red-500 text-white text-xs">{alerts.length}</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="history" className="flex items-center gap-2">
            <History className="w-4 h-4" />
            <span className="hidden sm:inline">History</span>
          </TabsTrigger>
        </TabsList>

        <TabsContent value="analyze" className="mt-6">
          {renderAnalysisTypeSelector()}
          {renderUploadArea()}
          {renderResults()}
        </TabsContent>

        <TabsContent value="alerts" className="mt-6">
          {renderAlerts()}
        </TabsContent>

        <TabsContent value="history" className="mt-6">
          {renderHistory()}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AdvancedSafetyAI;
